import React from 'react';
import { NavItem } from '../../framework/nav/NavItem';
import EntryPoints from '../../framework/nav/EntryPoints';
import { motion } from 'motion/react';
import './appsStyles.css';

const unsplashLogo = 'https://i.postimg.cc/nhb9pXs0/Unsplash-For-Confluence.png';
const oxalLogo = 'https://i.postimg.cc/1tJDzyx4/oxal-Green.png';
const qrCodeProLogo = 'https://i.postimg.cc/4dNhqjKG/QR-Code-Pro3.png';

interface AppCardProps {
  logo: string;
  name: string;
  tagline: string;
  description: string;
  link: string;
  marketplaceLink: string;
}

const AppCard = ({ logo, name, tagline, description, link, marketplaceLink }: AppCardProps) => {
  return (
    <motion.div
      className="app-card"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: 'easeOut' }}
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
    >
      <a href={link} className="app-card-inner" aria-label={`View ${name}`}>
        <div className="app-card-header">
          <img className="app-card-logo" src={logo} alt={`${name} logo`} />
          <div>
            <h3 className="app-card-name">{name}</h3>
            <p className="app-card-tagline">{tagline}</p>
          </div>
        </div>
        <p className="app-card-description">{description}</p>
        <span className="app-card-cta">View details →</span>
      </a>
      <a
        href={marketplaceLink}
        className="app-card-marketplace"
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        View on Atlassian Marketplace ↗
      </a>
    </motion.div>
  );
};

const AppsView = () => {
  return (
    <div className="apps-page">
      <motion.div
        className="apps-header"
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <h1>Apps</h1>
        <p className="apps-subtitle">
          A collection of Atlassian Marketplace apps built for Confluence — designed to be simple, powerful, and privacy-friendly.
        </p>
      </motion.div>

      <div className="apps-grid">
        <AppCard
          logo={unsplashLogo}
          name="Unsplash for Confluence"
          tagline="Beautiful photos, right in Confluence"
          description="Add stunning, royalty-free photos from Unsplash directly into your Confluence pages. Choose from millions of professional photos with no licensing restrictions."
          link="/#unsplash-for-confluence"
          marketplaceLink="https://marketplace.atlassian.com/apps/1236708?tab=overview&hosting=cloud"
        />
        <AppCard
          logo={oxalLogo}
          name="Oxal"
          tagline="Seamless Spreadsheets, Smarter Collaboration"
          description="Embed dynamic, fully customisable spreadsheets directly into Confluence pages. Advanced formatting, real-time editing, and API-driven functionality — all without leaving Confluence."
          link="/#oxal"
          marketplaceLink="https://marketplace.atlassian.com/apps/1236688/oxal-spreadsheets-for-confluence?hosting=cloud&tab=overview"
        />
        <AppCard
          logo={qrCodeProLogo}
          name="QR Code Pro"
          tagline="Effortless QR codes for any URL"
          description="Quickly generate and customise sleek QR codes for any URL, seamlessly integrated into your Confluence pages. Boost collaboration and streamline information sharing."
          link="/#qrCodePro"
          marketplaceLink="https://marketplace.atlassian.com/apps/1236040/qr-code-pro?hosting=cloud&tab=overview"
        />
      </div>
    </div>
  );
};

class AppsEntryPoints implements EntryPoints {

  renderFullView = (navItem: NavItem, secondaryNavItems: string[], width: number, height: number): any => {
    return <AppsView />;
  }

  renderIntroView = (navItem: NavItem, secondaryNavItems: string[]): any => {
    return <span>Apps</span>;
  }

}

const appsEntryPoints = new AppsEntryPoints();
export default appsEntryPoints;
